// lib/date.js

// 🔥 HOY en formato seguro (SIN timezone issues)
export const getLocalDateString = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// 🔥 Parse seguro SOLO para mostrar (SIEMPRE local)
export const parseLocalDate = (dateInput) => {
  if (!dateInput) return null;

  // 🔹 STRING (caso principal)
  if (typeof dateInput === "string") {
    const clean = dateInput.slice(0, 10);

    if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return null;

    const [year, month, day] = clean.split("-").map(Number);

    // 🔥 FORZAR HORA LOCAL 00:00 (clave)
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  // 🔹 DATE
  if (dateInput instanceof Date) {
    if (isNaN(dateInput)) return null;

    return new Date(
      dateInput.getFullYear(),
      dateInput.getMonth(),
      dateInput.getDate(),
      0, 0, 0, 0
    );
  }

  // 🔹 TIMESTAMP
  if (typeof dateInput === "number") {
    const d = new Date(dateInput);
    if (isNaN(d)) return null;

    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      0, 0, 0, 0
    );
  }

  return null;
};

// 🔥 Comparación segura (SIN Date)
export const isSameDate = (a, b) => {
  if (!a || !b) return false;
  return a.slice(0, 10) === b.slice(0, 10);
};

// 🔥 Formato inteligente (Hoy / Mañana)
export const formatSmartDate = (dateStr) => {
  if (!dateStr) return "";

  // ISO timestamp (contains 'T'): use local date parts to avoid UTC midnight drift.
  // e.g. "2026-05-13T05:00:00Z" at UTC-6 is still May 12 locally — slice(0,10) would give wrong date.
  const clean =
    typeof dateStr === "string" && dateStr.includes("T")
      ? (() => {
          const d = new Date(dateStr);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        })()
      : dateStr.slice(0, 10);

  const today = getLocalDateString();

  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);

  const tomorrow = `${tomorrowDate.getFullYear()}-${String(
    tomorrowDate.getMonth() + 1
  ).padStart(2, "0")}-${String(tomorrowDate.getDate()).padStart(2, "0")}`;

  if (clean === today) return "Hoy";
  if (clean === tomorrow) return "Mañana";

  const d = parseLocalDate(clean);
  if (!d) return "";

  return d.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
  });
};

// 🔥 Devuelve cuántos años han pasado desde la fecha indicada
export const getYearsSince = (dateInput, referenceDate = new Date()) => {
  const d = parseLocalDate(dateInput);
  const ref = parseLocalDate(referenceDate) || parseLocalDate(getLocalDateString());
  if (!d || !ref) return null;

  let years = ref.getFullYear() - d.getFullYear();
  const beforeBirthday =
    ref.getMonth() < d.getMonth() ||
    (ref.getMonth() === d.getMonth() && ref.getDate() < d.getDate());

  if (beforeBirthday) {
    years -= 1;
  }

  return years >= 0 ? years : null;
};

// 🔥 Forma un texto amigable para cumpleaños o aniversarios
export const formatYearsLabel = (dateInput, type = "birthday") => {
  const years = getYearsSince(dateInput);
  if (years === null) return null;

  if (type === "marriage") {
    return `${years} años de matrimonio`;
  }

  if (type === "baptism") {
    if (years === 0) return "Recién bautizado";
    return `${years} ${years === 1 ? "año" : "años"} de bautizado`;
  }

  return `Cumple ${years} años`;
};

// 🔥 Devuelve el número de días transcurridos desde una fecha hasta hoy (a medianoche)
export const getDaysSince = (dateString) => {
  if (!dateString) return null;
  // Asegurarse de tomar solo la parte YYYY-MM-DD, en caso de que sea una cadena ISO
  const cleanDateString = dateString.split("T")[0];
  const [y, m, d] = cleanDateString.split("-").map(Number);
  const visitDate = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = today - visitDate;
  return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24))); // Asegura que los días no sean negativos
};

// 🔥 Verifica si una fecha coincide con el día y mes de hoy
export const isAnnualEventToday = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  const [, m, d] = dateStr.split("-");
  const todayM = String(today.getMonth() + 1).padStart(2, "0");
  const todayD = String(today.getDate()).padStart(2, "0");
  return m === todayM && d === todayD;
};

// 🔥 Verifica si un evento anual caerá en los próximos N días
export const isAnnualEventUpcoming = (dateStr, days = 5) => {
  if (!dateStr) return false;
  const today = parseLocalDate(getLocalDateString());
  const [, month, day] = dateStr.split("-").map(Number);
  const refYear = today.getFullYear();
  
  let nextOccurrence = new Date(refYear, month - 1, day, 0, 0, 0, 0);
  if (nextOccurrence < today) {
    nextOccurrence = new Date(refYear + 1, month - 1, day, 0, 0, 0, 0);
  }
  const limit = new Date(today);
  limit.setDate(today.getDate() + days);
  return nextOccurrence >= today && nextOccurrence <= limit;
};

// 🔥 Formato corto: "15 Mar"
export const formatShort = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d) return "";
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
};

// 🔥 Formato mediano: "15 mar 2025"
export const formatMedium = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d) return "";
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
};

// 🔥 Formato completo: "lunes, 15 de marzo de 2025"
export const formatFull = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d) return "";
  return d.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// 🔥 Formato relativo: "hoy", "ayer", "hace 3 días", "hace 2 semanas", "hace 1 mes"
export const formatRelative = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d) return "";
  const today = parseLocalDate(getLocalDateString());
  const diffMs = today - d;
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return "hoy";
  if (days === 1) return "ayer";
  if (days < 7) return `hace ${days} días`;
  if (days < 14) return "hace 1 semana";
  if (days < 30) return `hace ${Math.floor(days / 7)} semanas`;
  if (days < 60) return "hace 1 mes";
  if (days < 365) return `hace ${Math.floor(days / 30)} meses`;
  const years = Math.floor(days / 365);
  return years === 1 ? "hace 1 año" : `hace ${years} años`;
};

export const isDateOverdue = (dateStr, timeStr) => {
  if (!dateStr) return false;
  const today = getLocalDateString();
  
  if (dateStr < today) return true;
  
  if (dateStr === today && timeStr) {
    const now = new Date();
    const [h, m] = timeStr.split(":").map(Number);
    const visitTime = new Date();
    visitTime.setHours(h, m, 0, 0);
    return visitTime < now;
  }
  
  return false;
};